import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import { getServerUrl } from '../../config/apiConfig';
import teacherService from '../../services/api/teacher';
import studentService from '../../services/api/student';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { useConfirm } from '../../hooks/useConfirm';
import { getImageUrl } from '../../config/apiConfig';

// Icons
import { 
  Clock, 
  Users, 
  Play, 
  Edit, 
  Trash2, 
  Plus,
  ArrowRight,
  Video,
  Calendar,
  Eye,
  BarChart,
  CheckCircle2,
  XCircle,
  PlayCircle
} from 'lucide-react';

// Video Utilities
import {
  formatDurationDisplay,
  getVideoThumbnail,
  getVideoEmbedUrl,
  getLessonDuration,
  getLessonViews
} from '../../utils/videoUtils';

const TeacherCourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const SERVER_BASE_URL = getServerUrl();
  const [currentPage, setCurrentPage] = useState(1);
  const [videosPerPage] = useState(6);


  // دالة حساب إجمالي مدة الكورس مع fallback logic (ترجع الثواني)
  const calculateTotalCourseDuration = useMemo(() => {
    return (course) => {
      if (!course) return 0;
      
      // أولوية: استخدام total_duration_seconds من API
      if (course.total_duration_seconds && parseInt(course.total_duration_seconds) > 0) {
        return parseInt(course.total_duration_seconds); // بالثواني مباشرة
      }
      
      // ثانوية: استخدام حقول المدة المباشرة من الكورس
      if (course.duration_hours || course.duration_minutes) {
        const hours = parseInt(course.duration_hours) || 0;
        const minutes = parseInt(course.duration_minutes) || 0;
        return (hours * 3600) + (minutes * 60); // تحويل لثواني
      }
      
      // ثالثية: استخدام حقل duration_seconds
      if (course.duration_seconds) {
        return course.duration_seconds; // بالثواني مباشرة
      }
      
      // رابعة: حساب من الدروس
      if (course.lessons && course.lessons.length > 0) {
        const totalSeconds = course.lessons.reduce((total, lesson) => {
          return total + (getLessonDuration(lesson) || 0); // getLessonDuration يرجع seconds
        }, 0);
        return totalSeconds; // ترجع الثواني
      }
      
      return 0;
    };
  }, []);

  // دالة لتنسيق عرض المدة الإجمالية للكورس
  const formatTotalCourseDuration = useMemo(() => {
    return (course) => {
      if (!course) return '--:--';
      const totalSeconds = calculateTotalCourseDuration(course);
      return formatDurationDisplay(totalSeconds); // تمرر الثواني مباشرة
    };
  }, [calculateTotalCourseDuration]);

  // جلب بيانات الكورس للمدرس
  const { data: course, isLoading, error } = useQuery(
    ['teacher-course', courseId],
    () => teacherService.getCourse(courseId).then(data => {
      return data;
    }),
    {
      enabled: !!courseId,
      onError: (error) => {
        if (error.response?.status === 404) {
          navigate('/teacher/courses');
        }
      }
    }
  );

  // Get course thumbnail URL using getImageUrl (compute after `course` is defined)
  const thumbnailUrl = course?.thumbnail ? getImageUrl(course.thumbnail) : null;
  const hasThumbnail = !!course?.thumbnail;

  // جلب فيديوهات الكورس
  const { data: videosData } = useQuery(
    ['course-videos', courseId],
    () => teacherService.getCourseVideos(courseId),
    {
      enabled: !!courseId,
      select: (data) => data.videos || []
    }
  );

  // Fetch analytics for course videos (aggregate views)
  const [lessonAnalytics, setLessonAnalytics] = React.useState({});

  React.useEffect(() => {
    const ids = (videosData || []).map(v => v.id);
    if (!ids || ids.length === 0) return;

    const idsToFetch = ids.filter(id => !(id in lessonAnalytics));
    if (idsToFetch.length === 0) return;

    const fetchAll = async () => {
      const map = {};
      for (const id of idsToFetch) {
        try {
          const res = await studentService.getLessonAnalytics(id);
          map[id] = res?.data?.stats?.views ?? null;
        } catch (err) {
          map[id] = null;
        }
      }
      setLessonAnalytics(prev => ({ ...prev, ...map }));
    };

    fetchAll();
  }, [videosData, lessonAnalytics]);

  // Pagination logic for videos
  const indexOfLastVideo = currentPage * videosPerPage;
  const indexOfFirstVideo = indexOfLastVideo - videosPerPage;
  const currentVideos = videosData?.slice(indexOfFirstVideo, indexOfLastVideo);
  const totalPages = Math.ceil((videosData?.length || 0) / videosPerPage);

  // Reset page when filters change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Mutation لحذف الكورس
  const deleteCourseMutation = useMutation(
    (courseId) => teacherService.deleteCourse(courseId),
    {
      onSuccess: () => {
        toast.success('تم حذف الكورس بنجاح');
        queryClient.invalidateQueries('teacher-courses');
        navigate('/teacher/courses');
      },
      onError: (error) => {
        toast.error('فشل في حذف الكورس');
      }
    }
  );

  // Event Handlers
  const handleEditCourse = () => {
    navigate(`/teacher/courses/${courseId}/edit`);
  };

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const handlePlayVideo = (video) => {
    
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  const handleCloseVideoModal = () => {
    setSelectedVideo(null);
    setShowVideoModal(false);
  };

  const handleDeleteVideo = async (video) => {
    const confirmed = await confirm({
      title: 'تأكيد الحذف النهائي',
      message: `هل أنت متأكد من حذف الفيديو "${video.title}"؟ هذا الإجراء سيؤدي لإزالة الفيديو من محتوى الكورس نهائياً.`,
      type: 'danger'
    });
    
    if (confirmed) {
      teacherService.deleteCourseVideo(courseId, video.id)
        .then(response => {
          queryClient.invalidateQueries(['course-videos', courseId]);
          toast.success('تم حذف الفيديو بنجاح');
        })
        .catch(error => {
          toast.error('فشل حذف الفيديو: ' + (error.response?.data?.error || 'خطأ في الخادم'));
        });
    }
  };

  const handleDeleteCourse = async () => {
    const confirmed = await confirm({
      title: 'حذف الكورس بالكامل',
      message: 'تحذير! هل أنت متأكد من حذف هذا الكورس؟ سيتم حذف جميع الفيديوهات والاشتراكات المتعلقة به. هذا الإجراء لا يمكن التراجع عنه.',
      type: 'danger'
    });
    
    if (confirmed) {
      deleteCourseMutation.mutate(courseId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحميل تفاصيل الكورس...</p>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-6">
        <div className="bg-white rounded-[2rem] shadow-xl p-10 text-center border border-red-100">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">تعذر العثور على الكورس</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">ربما تم حذف هذا الكورس أو أنك لا تملك صلاحية الوصول إليه.</p>
          <button
            onClick={() => navigate('/teacher/courses')}
            className="inline-flex items-center px-10 py-3 bg-primary-600 text-white rounded-2xl font-bold hover:bg-primary-700 transition-all shadow-lg"
          >
            العودة للقائمة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-10">
      
      {/* Navigation & Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <button
            onClick={() => navigate('/teacher/courses')}
            className="group flex items-center text-gray-500 hover:text-primary-600 font-bold transition-all mb-2"
            >
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            العودة لإدارة الكورسات
            </button>
            <h1 className="text-4xl font-black text-gray-900 tracking-tight">إدارة محتوى الكورس</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
            <button
                onClick={() => navigate(`/teacher/create-lesson?courseId=${courseId}`)}
                className="bg-green-600 text-white px-6 py-3 rounded-2xl font-black hover:bg-green-700 transition-all shadow-lg shadow-green-100 flex items-center gap-2"
            >
                <Plus size={20} /> إنشاء درس جديد
            </button>
            <button
                onClick={handleEditCourse}
                className="bg-blue-600 text-white px-6 py-3 rounded-2xl font-black hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 flex items-center gap-2"
            >
                <Edit size={20} /> تعديل البيانات
            </button>
            <button
                onClick={handleDeleteCourse}
                disabled={deleteCourseMutation.isLoading}
                className="bg-red-50 text-red-600 px-6 py-3 rounded-2xl font-black hover:bg-red-600 hover:text-white transition-all flex items-center gap-2"
            >
                <Trash2 size={20} /> {deleteCourseMutation.isLoading ? 'جاري الحذف...' : 'حذف الكورس'}
            </button>
        </div>
      </div>
        
      {/* Course Hero Card */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group">
        <div className="relative h-72 bg-gray-100">
          {hasThumbnail && thumbnailUrl ? (
            <img 
              src={thumbnailUrl} 
              alt={course.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              onError={(e) => {
                e.target.src = '/default_course.png';
              }}
            />
          ) : (
            <img src="/default_course.png" alt={course.title} className="w-full h-full object-cover" />
          )}
          
          <div className="absolute top-6 left-6">
            <span className={`inline-flex items-center gap-1.5 px-6 py-2 rounded-xl font-black text-xs shadow-xl backdrop-blur-md ${
              course.is_active 
                ? 'bg-green-500/90 text-white' 
                : 'bg-gray-800/90 text-white'
            }`}>
              {course.is_active ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              {course.is_active ? 'الكورس نشط للطلاب' : 'الكورس مخفي حالياً'}
            </span>
          </div>
        </div>
        
        <div className="p-8 lg:p-10">
          <div className="mb-10">
            <h2 className="text-3xl font-black text-gray-900 mb-3">{course.title}</h2>
            <p className="text-gray-500 text-lg leading-relaxed max-w-4xl">{course.description}</p>
          </div>
          
            {/* Dashboard Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'الطلاب المشتركون', value: course.student_count || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'مدة الكورس', value: formatTotalCourseDuration(course), icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'إجمالي الفيديوهات', value: videosData?.length || 0, icon: PlayCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'مشاهدات الكورس', value: (videosData || []).reduce((sum, v) => sum + (lessonAnalytics[v.id] != null ? parseInt(lessonAnalytics[v.id],10) || 0 : 0), 0), icon: Eye, color: 'text-amber-500', bg: 'bg-amber-50' }
            ].map((stat, i) => (
              <div key={i} className="p-6 bg-gray-50 rounded-[2rem] border border-gray-100 flex items-center gap-5 group hover:bg-white hover:shadow-lg transition-all">
                <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl`}>
                  <stat.icon size={24} />
                </div>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest">{stat.label}</p>
                  <p className="text-2xl font-black text-gray-900 leading-none mt-1">{stat.value}</p>
                </div>
              </div>
            ))}
            </div>

          {/* Pills Section */}
          <div className="mt-10 flex flex-wrap gap-3">
              <span className="bg-primary-50 text-primary-600 px-5 py-2 rounded-xl text-xs font-black border border-primary-100 tracking-wider uppercase">
                {course.subject_name}
              </span>
              <span className="bg-gray-100 text-gray-600 px-5 py-2 rounded-xl text-xs font-black border border-gray-200 tracking-wider uppercase">
                {course.grade_name}
              </span>
              <span className="bg-gray-100 text-gray-600 px-5 py-2 rounded-xl text-xs font-black border border-gray-200 tracking-wider uppercase">
                {course.stage_name}
              </span>
          </div>
        </div>
      </div>
      
      {/* Videos List Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-10">
          <div className="space-y-1">
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                <div className="w-2 h-8 bg-primary-600 rounded-full"></div>
                منهج الكورس الدراسي
            </h2>
            <p className="text-gray-400 text-sm font-medium">قم بترتيب، تشغيل، أو حذف دروس الكورس الحالية.</p>
          </div>
          <button
            onClick={() => navigate(`/teacher/create-lesson?courseId=${courseId}`)}
            className="bg-primary-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-primary-700 transition-all shadow-xl shadow-primary-100 flex items-center gap-2"
          >
            <Plus size={20} /> إنشاء درس جديد
          </button>
        </div>
        
        {currentVideos && currentVideos.length > 0 ? (
          <div className="space-y-4">
            {currentVideos.map((video, index) => (
              <div 
                key={video.id} 
                onClick={() => handlePlayVideo(video)}
                className="group border border-gray-50 bg-gray-50/50 rounded-[2rem] p-5 hover:bg-white hover:shadow-xl hover:border-primary-100 transition-all cursor-pointer relative overflow-hidden"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex flex-col sm:flex-row items-center gap-6 flex-1">
                    {/* Video Thumbnail Wrapper */}
                    <div className="relative w-full sm:w-40 h-24 bg-gray-200 rounded-2xl overflow-hidden flex-shrink-0 shadow-inner">
                      {video.video_url ? (
                        <>
                          <img
                            src={getVideoThumbnail(video.video_url, video) || '/default_video.png'}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={(e) => {
                              if (e.target.src.includes('/default_video.png')) return;
                              e.target.onerror = null;
                              e.target.src = '/default_video.png';
                            }}
                            onLoad={(e) => {
                              if (e.target) {
                                e.target.style.opacity = '1';
                              }
                            }}
                          />
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <Play className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <Play size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" />
                      </div>
                      <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md px-2 py-1 rounded-lg text-[8px] font-black text-white uppercase tracking-widest">
                        {video.video_url?.includes('iframe.mediadelivery.net') ? 'Bunny' : video.video_type === 'external' ? 'خارجي' : 'محلي'}
                        <Play size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" />
                      </div>
                    </div>

                    <div className="space-y-1 text-center sm:text-right flex-1">
                      <h3 className="font-black text-gray-900 text-lg group-hover:text-primary-600 transition-colors">
                        {index + 1}. {video.title}
                      </h3>
                      <div className="flex flex-wrap justify-center sm:justify-start items-center gap-4 text-xs font-bold text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock size={14} className="text-primary-500" /> 
                          {formatDurationDisplay(getLessonDuration(video))}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} className="text-primary-500" /> 
                          {getArabicDate(video.created_at)}
                        </span>
                        <span className="bg-gray-200 text-gray-500 px-2 py-0.5 rounded uppercase tracking-tighter">
                          {video.video_type}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                          {getLessonViews(video)} مشاهدة
                        </span>
                      </div>
                      {video.description && (
                        <p className="text-gray-500 text-sm line-clamp-1 mt-2">{video.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-3">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handlePlayVideo(video); }}
                      className="w-12 h-12 flex items-center justify-center bg-white text-emerald-600 rounded-2xl shadow-sm border border-gray-100 hover:bg-emerald-600 hover:text-white transition-all transform active:scale-95"
                      title="معاينة الفيديو"
                    >
                      <Eye size={20} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleDeleteVideo(video); }}
                      className="w-12 h-12 flex items-center justify-center bg-white text-red-600 rounded-2xl shadow-sm border border-gray-100 hover:bg-red-600 hover:text-white transition-all transform active:scale-95"
                      title="حذف الدرس"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-gray-50 rounded-[3rem] border border-dashed border-gray-200">
            <Video className="w-20 h-20 text-gray-200 mx-auto mb-6" />
            <h3 className="text-xl font-black text-gray-900 mb-2">لا يوجد محتوى دراسي</h3>
            <p className="text-gray-500 max-w-sm mx-auto mb-10">ابدأ ببناء منهج الكورس الخاص بك عن طريق إنشاء أول درس للطلاب.</p>
            <button
              onClick={() => navigate(`/teacher/create-lesson?courseId=${courseId}`)}
              className="bg-primary-600 text-white px-12 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center gap-3 mx-auto"
            >
              <Plus size={24} /> إنشاء أول درس
            </button>
          </div>
        )}
      </div>
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            السابق
          </button>
          
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                className={`w-10 h-10 rounded-xl font-medium transition-all ${
                  currentPage === index + 1
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            التالي
          </button>
        </div>
      )}
      
      {/* Enhanced Video Preview Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-5xl w-full relative overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 text-emerald-600 p-2 rounded-xl">
                    <Video size={20} />
                </div>
                <h3 className="text-xl font-black text-gray-900">{selectedVideo.title}</h3>
              </div>
              <button
                onClick={handleCloseVideoModal}
                className="w-10 h-10 flex items-center justify-center bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-900 rounded-2xl transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              <div className="aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl ring-4 ring-gray-50">
                {selectedVideo.video_url?.includes('iframe.mediadelivery.net') ? (
                  <iframe
                    src={selectedVideo.video_url}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="فيديو الدرس"
                  />
                ) : selectedVideo.video_type === 'external' ? (
                  <iframe
                    src={getVideoEmbedUrl(selectedVideo.video_url)}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="فيديو الدرس"
                  />
                ) : (
                  <video
                    src={`${SERVER_BASE_URL}${selectedVideo.video_url}`}
                    className="w-full h-full"
                    controls
                    autoPlay
                  >
                    نعتذر، متصفحك لا يدعم تشغيل هذا النوع من الفيديوهات.
                  </video>
                )}
              </div>
              
              {/* Extra Info in Modal */}
              <div className="mt-8 flex flex-col md:flex-row md:items-start justify-between gap-6">
                <div className="space-y-2 flex-1">
                    <h4 className="font-black text-gray-900 flex items-center gap-2 uppercase tracking-tighter text-sm">
                        <BarChart size={16} className="text-primary-600" /> وصف الدرس وتفاصيله
                    </h4>
                    <p className="text-gray-500 leading-relaxed">{selectedVideo.description || 'لا يوجد وصف إضافي لهذا الدرس.'}</p>
                </div>
                
                <div className="flex flex-wrap gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                    <div className="flex flex-col items-center px-4">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">المدة</span>
                        <span className="text-sm font-black text-gray-900">
                          {formatDurationDisplay(getLessonDuration(selectedVideo))}
                        </span>
                    </div>
                    <div className="w-[1px] h-8 bg-gray-200 self-center hidden sm:block"></div>
                    <div className="flex flex-col items-center px-4">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">الرفع</span>
                        <span className="text-sm font-black text-gray-900">{getArabicDate(selectedVideo.created_at)}</span>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Components */}
      <ConfirmDialogComponent />
    </div>
  );
};

export default TeacherCourseDetail;